import { useState, type CSSProperties, type ReactNode } from "react";
import type { OperationalState, ProductionReadyCheckItem } from "../operationalState";
import { LABEL } from "../terminology";
import { formatVerificationAge } from "../verification";
import { RuntimeDoctorStatus } from "./RuntimeDoctorStatus";
import { PlatformHealthPanel } from "./PlatformHealthPanel";
import { CollapsibleSection } from "./CollapsibleSection";
import {
  operatorFacingMessage,
  shortOperatorRecommendation,
} from "./operatorCopy";

export type OperationalTruthPanelProps = {
  state: OperationalState;
  /** Ops asks "what"; Control Plane asks "why". */
  surface: "ops" | "controlPlane";
  /** Navigate / scroll to existing Runtime Doctor report — never a duplicate page. */
  onViewDoctorReport?: () => void;
  /** Optional: open a remaining readiness check for action. */
  onOpenReadinessItem?: (item: ProductionReadyCheckItem) => void;
  className?: string;
  style?: CSSProperties;
};

/**
 * Shared operational truth chrome with progressive disclosure.
 * Level 1 executive summary always visible; DNS/SSL/KB diagnostics behind expand.
 */
export function OperationalTruthPanel({
  state,
  surface,
  onViewDoctorReport,
  onOpenReadinessItem,
  className = "",
  style,
}: OperationalTruthPanelProps): ReactNode {
  const framing =
    surface === "ops" ? state.framing.ops : state.framing.controlPlane;
  const doctor = state.runtimeDoctor;
  const readiness = state.productionReadiness;
  const recommendation = shortOperatorRecommendation(
    state.platformHealth.recommendedAction ?? doctor.recommendedAction,
  );

  return (
    <div
      className={`operational-truth-panel ${className}`.trim()}
      style={style}
      data-slug={state.slug}
      data-surface={surface}
    >
      <p
        style={{
          margin: "0 0 0.75rem",
          fontSize: "0.75rem",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "#94a3b8",
        }}
      >
        {framing}
      </p>

      {/* Level 1 — Executive summary */}
      <PlatformHealthPanel verdict={state.platformHealth} />
      <dl
        style={{
          margin: "0.55rem 0 0",
          display: "grid",
          gap: "0.2rem",
          fontSize: "0.75rem",
          color: "#94a3b8",
        }}
      >
        <div>
          <dt style={{ display: "inline" }}>Runtime Doctor: </dt>
          <dd style={{ display: "inline", margin: 0, color: "#cbd5e1" }}>
            {doctor.currentResult}
          </dd>
        </div>
        <div>
          <dt style={{ display: "inline" }}>Last verification: </dt>
          <dd style={{ display: "inline", margin: 0, color: "#cbd5e1" }}>
            {(() => {
              const age = formatVerificationAge(state.lastVerification);
              return age
                ? `Verified ${age}`
                : state.lastVerification ?? LABEL.notAvailable;
            })()}
          </dd>
        </div>
        <div>
          <dt style={{ display: "inline" }}>Confidence: </dt>
          <dd style={{ display: "inline", margin: 0, color: "#cbd5e1" }}>
            {state.confidence.label}
          </dd>
        </div>
        {recommendation ? (
          <div>
            <dt style={{ display: "inline" }}>Action: </dt>
            <dd style={{ display: "inline", margin: 0, color: "#fcd34d" }}>{recommendation}</dd>
          </div>
        ) : null}
        {state.incident ? (
          <div>
            <dt style={{ display: "inline" }}>Incident: </dt>
            <dd style={{ display: "inline", margin: 0, color: "#cbd5e1" }}>
              {state.incident.problem}
            </dd>
          </div>
        ) : null}
      </dl>

      <CollapsibleSection
        title="Runtime Doctor"
        summary={
          <>
            {doctor.currentResult}
            {doctor.rootCause
              ? ` · ${operatorFacingMessage(doctor.rootCause) ?? doctor.rootCause}`
              : ""}
          </>
        }
        defaultOpen={false}
        style={{
          marginTop: "0.85rem",
          padding: "0.55rem 0.65rem",
          borderRadius: "0.5rem",
          border: "1px solid #1e293b",
          background: "rgba(2, 6, 23, 0.35)",
        }}
      >
        <div
          id={`runtime-doctor-${state.slug}`}
          style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}
        >
          <RuntimeDoctorStatus status={doctor.currentResult} />
        </div>
        {doctor.rootCause ? (
          <p style={{ margin: "0.35rem 0 0", fontSize: "0.8rem", color: "#cbd5e1" }}>
            <span style={{ color: "#94a3b8" }}>Root cause: </span>
            {operatorFacingMessage(doctor.rootCause) ?? doctor.rootCause}
          </p>
        ) : null}
        {doctor.recommendedAction ? (
          <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem", color: "#cbd5e1" }}>
            <span style={{ color: "#94a3b8" }}>Recommended action: </span>
            {shortOperatorRecommendation(doctor.recommendedAction) ?? doctor.recommendedAction}
          </p>
        ) : null}
        {onViewDoctorReport ? (
          <button
            type="button"
            onClick={onViewDoctorReport}
            style={{
              marginTop: "0.55rem",
              border: "none",
              background: "transparent",
              color: "#7dd3fc",
              fontSize: "0.8rem",
              padding: 0,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {doctor.reportLabel}
          </button>
        ) : null}
      </CollapsibleSection>

      <CollapsibleSection
        title="Production readiness"
        summary={`${readiness.summary} (${readiness.readiness})`}
        defaultOpen={false}
        style={{ marginTop: "0.65rem" }}
      >
        {readiness.remaining.length > 0 ? (
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {readiness.remaining.map((item) => (
              <li
                key={item.id}
                style={{
                  marginTop: "0.35rem",
                  padding: "0.45rem 0.55rem",
                  borderRadius: "0.4rem",
                  border: "1px solid #1e293b",
                  background: "rgba(15, 23, 42, 0.5)",
                }}
              >
                {onOpenReadinessItem ? (
                  <button
                    type="button"
                    onClick={() => onOpenReadinessItem(item)}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#7dd3fc",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      padding: 0,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {item.label}
                  </button>
                ) : (
                  <strong style={{ fontSize: "0.8rem", color: "#e2e8f0" }}>{item.label}</strong>
                )}
                <div style={{ marginTop: "0.2rem", fontSize: "0.72rem", color: "#94a3b8" }}>
                  Current: {item.current}
                </div>
                <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                  Expected: {item.expected}
                </div>
                {item.recommendedAction ? (
                  <div style={{ fontSize: "0.72rem", color: "#cbd5e1" }}>
                    Action:{" "}
                    {shortOperatorRecommendation(item.recommendedAction) ??
                      item.recommendedAction}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ margin: 0, fontSize: "0.8rem", color: "#6ee7b7" }}>All checks passed</p>
        )}
      </CollapsibleSection>

      <DnsStatusCard state={state} />
      <SslStatusCard state={state} />
      <KnowledgeStatusCard state={state} />

      {state.verificationTimeline.length > 0 ? (
        <CollapsibleSection
          title="Evidence timeline"
          summary={`${state.verificationTimeline.length} verification event(s)`}
          defaultOpen={false}
          style={{ marginTop: "0.65rem" }}
        >
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: "none",
              fontSize: "0.72rem",
              color: "#cbd5e1",
            }}
          >
            {state.verificationTimeline.map((entry) => (
              <li
                key={entry.id}
                style={{
                  marginTop: "0.3rem",
                  paddingTop: "0.3rem",
                  borderTop: "1px solid #1e293b",
                }}
              >
                <span style={{ color: "#94a3b8" }}>
                  {entry.observedAt ? entry.observedAt.replace("T", " ").slice(0, 19) : "—"}
                </span>
                {" · "}
                {entry.title}
                <div style={{ marginTop: "0.15rem", color: "#94a3b8" }}>
                  {operatorFacingMessage(entry.detail) ?? entry.detail}
                </div>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      ) : null}
    </div>
  );
}

function DnsStatusCard({ state }: { state: OperationalState }): ReactNode {
  const d = state.dns;
  const resolution =
    d.resolutionMode === "cloudflare_proxy" || d.proxiedViaCloudflare === true
      ? "Cloudflare"
      : d.resolutionMode === "direct" || d.proxiedViaCloudflare === false
        ? "Direct"
        : null;
  const configured =
    d.state && d.state !== "not_configured"
      ? d.state === "verified" && d.ok
        ? "Configured"
        : d.state === "failed"
          ? "Failed"
          : "Configured"
      : "Not configured";
  const application =
    d.applicationResponds === true
      ? "Healthy"
      : d.applicationResponds === false
        ? "Unreachable"
        : d.state === "verified" && d.ok
          ? "Healthy"
          : null;
  const proxyActive =
    d.resolutionMode === "cloudflare_proxy" || d.proxiedViaCloudflare === true
      ? "Proxy Active"
      : null;
  const verifiedAge = formatVerificationAge(
    d.verifications[0]?.timestamp ?? state.lastVerification,
  );
  const lastVerified = verifiedAge ? `Verified ${verifiedAge}` : null;
  const evidenceLine = operatorFacingMessage(d.evidence ?? d.detail);

  const summary = (
    <>
      <span>{configured}</span>
      {resolution ? <span>{` · ${resolution}`}</span> : null}
      {proxyActive ? <span>{` · ${proxyActive}`}</span> : null}
      {application ? (
        <span style={{ display: "block", marginTop: "0.15rem" }}>
          Application {application}
        </span>
      ) : null}
      {lastVerified ? (
        <span style={{ display: "block", marginTop: "0.15rem", color: "#64748b", fontSize: "0.72rem" }}>
          {lastVerified}
        </span>
      ) : null}
    </>
  );

  return (
    <CollapsibleSection
      title="DNS"
      summary={summary}
      defaultOpen={false}
      toggleLabel={undefined}
      style={{ marginTop: "0.65rem" }}
    >
      <p style={{ margin: 0, fontSize: "0.8rem", color: "#cbd5e1" }}>
        {configured}
        {resolution ? ` · ${resolution}` : ""}
        {proxyActive ? ` · ${proxyActive}` : ""}
      </p>
      {application ? (
        <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem", color: "#cbd5e1" }}>
          Application {application}
        </p>
      ) : null}
      {lastVerified ? (
        <p style={{ margin: "0.2rem 0 0", fontSize: "0.72rem", color: "#64748b" }}>
          {lastVerified}
        </p>
      ) : null}
      <DiagnosticsBlock>
        <DiagLine label="Hostname" value={d.hostname} />
        <DiagLine label="State" value={d.state} />
        <DiagLine
          label="Resolution mode"
          value={
            resolution === "Cloudflare"
              ? "Cloudflare Proxy"
              : resolution === "Direct"
                ? "Direct (DNS only)"
                : null
          }
        />
        <DiagLine label="Expected origin IP" value={d.expectedIp} />
        <DiagLine
          label="Resolved addresses"
          value={d.resolvedIps.length ? d.resolvedIps.join(", ") : null}
        />
        <DiagLine label="HTTPS reachable" value={boolLabel(d.httpsReachable)} />
        <DiagLine label="TLS valid" value={boolLabel(d.tlsValid)} />
        <DiagLine label="Application responds" value={boolLabel(d.applicationResponds)} />
        <DiagLine label="Evidence" value={evidenceLine} />
        {d.verifications.map((v) => (
          <DiagLine
            key={`${v.component}-${v.timestamp}`}
            label={`${v.component} verification`}
            value={
              operatorFacingMessage(
                v.evidence.map((e) => `${e.label}: ${e.value}`).join(" · ") ||
                  v.verification_method,
              ) ?? v.verification_method
            }
          />
        ))}
      </DiagnosticsBlock>
    </CollapsibleSection>
  );
}

function SslStatusCard({ state }: { state: OperationalState }): ReactNode {
  const s = state.ssl;
  const status =
    !s.state || s.state === "not_configured"
      ? "Not configured"
      : s.state === "verified" && s.ok
        ? "Valid"
        : s.state === "expiring_soon"
          ? "Expiring soon"
          : s.state === "failed"
            ? "Failed"
            : s.state;
  const summary = (
    <>
      <span>{status}</span>
      {s.issuer ? <span>{` · ${s.issuer}`}</span> : null}
      {s.expiresAt ? (
        <span style={{ display: "block", marginTop: "0.15rem", color: "#64748b", fontSize: "0.72rem" }}>
          Expires {s.expiresAt}
          {s.daysRemaining != null ? ` (${s.daysRemaining}d)` : ""}
        </span>
      ) : null}
    </>
  );

  return (
    <CollapsibleSection title="SSL / TLS" summary={summary} defaultOpen={false} style={{ marginTop: "0.65rem" }}>
      <p style={{ margin: 0, fontSize: "0.8rem", color: "#cbd5e1" }}>{status}</p>
      <DiagnosticsBlock>
        <DiagLine label="Hostname" value={s.hostname} />
        <DiagLine label="Issuer" value={s.issuer} />
        <DiagLine label="Expires" value={s.expiresAt} />
        <DiagLine
          label="Days remaining"
          value={s.daysRemaining != null ? String(s.daysRemaining) : null}
        />
        <DiagLine
          label="Hostname matches"
          value={s.hostnameMatches == null ? null : s.hostnameMatches ? "Yes" : "No"}
        />
        <DiagLine
          label="Evidence"
          value={operatorFacingMessage(s.evidence ?? s.detail) ?? s.evidence ?? s.detail}
        />
      </DiagnosticsBlock>
    </CollapsibleSection>
  );
}

function KnowledgeStatusCard({ state }: { state: OperationalState }): ReactNode {
  const k = state.knowledge;
  const ready =
    k.ready == null ? LABEL.notAvailable : k.ready ? "Healthy" : "Not ready";
  const indexed =
    k.chunksIndexed != null
      ? `${k.chunksIndexed} chunks`
      : k.pdfCount != null
        ? `${k.pdfCount} PDF(s)`
        : null;
  const verifiedAge = formatVerificationAge(k.lastVerifiedAt);
  const lastVerified = verifiedAge ? `Verified ${verifiedAge}` : null;
  const summary = (
    <>
      <span>{ready}</span>
      {indexed ? <span>{` · ${indexed}`}</span> : null}
      {lastVerified ? (
        <span style={{ display: "block", marginTop: "0.15rem", color: "#64748b", fontSize: "0.72rem" }}>
          {lastVerified}
        </span>
      ) : null}
    </>
  );

  return (
    <CollapsibleSection
      title="Knowledge Base"
      summary={summary}
      defaultOpen={false}
      style={{ marginTop: "0.65rem" }}
    >
      <p style={{ margin: 0, fontSize: "0.8rem", color: "#cbd5e1" }}>{ready}</p>
      {lastVerified ? (
        <p style={{ margin: "0.2rem 0 0", fontSize: "0.72rem", color: "#64748b" }}>
          {lastVerified}
        </p>
      ) : null}
      <DiagnosticsBlock>
        <DiagLine
          label="Ready"
          value={k.ready == null ? LABEL.notAvailable : k.ready ? "Yes" : "No"}
        />
        <DiagLine
          label="PDF docs"
          value={k.pdfCount != null ? String(k.pdfCount) : null}
        />
        <DiagLine
          label="Chunks indexed"
          value={k.chunksIndexed != null ? String(k.chunksIndexed) : null}
        />
        <DiagLine
          label="Search verify"
          value={
            k.lastVerifyMessage ??
            (k.lastVerifyOk == null
              ? LABEL.notAvailable
              : k.lastVerifyOk
                ? "Verified"
                : "Failed")
          }
        />
        <DiagLine label="Detail" value={operatorFacingMessage(k.detail) ?? k.detail} />
      </DiagnosticsBlock>
    </CollapsibleSection>
  );
}

function DiagnosticsBlock({ children }: { children: ReactNode }): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: "0.45rem" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: 0,
          border: "none",
          background: "transparent",
          color: "#93c5fd",
          fontSize: "0.68rem",
          cursor: "pointer",
        }}
      >
        {open ? "Hide diagnostics" : "▶ Show diagnostics"}
      </button>
      {open ? <dl style={{ margin: "0.35rem 0 0", fontSize: "0.75rem" }}>{children}</dl> : null}
    </div>
  );
}

function DiagLine({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): ReactNode {
  if (value == null || value === "") return null;
  return (
    <div style={{ marginTop: "0.15rem" }}>
      <dt style={{ display: "inline", color: "#64748b" }}>{label}: </dt>
      <dd style={{ display: "inline", margin: 0, color: "#cbd5e1" }}>{value}</dd>
    </div>
  );
}

function boolLabel(value: boolean | null | undefined): string | null {
  if (value == null) return null;
  return value ? "Yes" : "No";
}
